<template>
  <AnimatedContainer>
    <Container tag="section" space-y>
      <!-- HEADING -->
      <h2
        v-if="data.section_title"
        class="slide-up mx-container mb-8 border-b-1.5 border-gray-500 pb-3 typo-heading-xs md:mx-container-lg"
      >
        {{ data.section_title }}
      </h2>
      <TextAppear
        v-if="data.heading"
        tag="h3"
        class="slide-up mb-12 px-container typo-heading-xl md:mb-20 md:px-container-lg lg:typo-heading-2xl"
        :text="data.heading"
      />

      <!-- PARALLAX -->
      <div ref="el" class="relative">
        <!-- IMAGE -->
        <!-- eslint-disable tailwindcss/migration-from-tailwind-2 -->
        <div
          class="relative aspect-portrait overflow-clip max-md:mr-container md:w-2/3 lg:aspect-square xl:aspect-landscape"
        >
          <!-- eslint-enable tailwindcss/migration-from-tailwind-2 -->
          <div
            ref="imageContainer"
            class="absolute top-[2.5%] h-[105%] w-full lg:top-[10%]"
          >
            <!--
            - h-[x%] to move only image in container
            - top-[x%] h-[x%] to move upper and lower edge of image during parallax
          -->
            <AppImage
              v-if="data.image"
              :src="data.image"
              loading="lazy"
              class="size-full object-cover"
              :style="`object-position: ${data.image.focus_css};`"
            />
          </div>
        </div>

        <!-- TEXT CONTAINER -->
        <div
          ref="textContainer"
          :class="[
            'relative -mt-40 ml-container ',
            'md:-mt-48 md:ml-auto md:w-180',
            'lg:-mt-60 lg:mr-container lg:w-210',
            'xl:-mt-80 xl:mr-container-md',
          ]"
        >
          <div class="xl:-translate-y-48">
            <div
              class="absolute inset-0 bg-gradient-beige-to-gray opacity-[0.97]"
            />
            <div
              class="relative py-10 pl-10 pr-6 md:pb-16 md:pl-24 md:pr-18 md:pt-20"
            >
              <div v-if="data.text_type">
                <dl class="nth-child max-w-text">
                  <template
                    v-for="item in data.description_list"
                    :key="item.id"
                  >
                    <dt
                      class="typo-body-sm sm:typo-body-md [&:nth-child(n+2)]:mt-16"
                    >
                      {{ item.title }}
                    </dt>
                    <dd class="typo-heading-md">{{ item.description }}</dd>
                  </template>
                </dl>
              </div>
              <div v-for="(item, index) in data.text_field" v-else :key="index">
                <!-- Text -->
                <div
                  v-if="item.type === 'text'"
                  class="wysiwyg-custom-text [&_h3]:mt-0"
                  v-html="item.text"
                />
                <!-- Button -->
                <div v-if="item.type === 'button'" class="slide-up max-w-text">
                  <Download
                    v-if="item.download"
                    class="my-4 w-fit"
                    :file="item"
                  />
                  <AppLink
                    v-if="!item.download && item.link"
                    :href="item.link"
                    :show-link-arrow="true"
                    class="my-8 flex typo-button-sm md:typo-button-md"
                    >{{ item.link_text }}</AppLink
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </AnimatedContainer>
</template>

<script setup lang="ts">
defineProps({
  data: { type: Object, required: true },
})

const el = ref<HTMLElement | null>(null)
const imageContainer = ref<HTMLElement | null>(null)
const textContainer = ref<HTMLElement | null>(null)
let ctx: any = null

onMounted(() => {
  initParallaxAnimation()
})

onUnmounted(() => {
  ctx.revert()
})

const initParallaxAnimation = () => {
  ctx = useGsap.context(
    () => {
      const tl = useGsap.timeline({})
      const mm = useGsap.matchMedia()
      const breakpoint = 1024

      const elHeight = el.value?.clientHeight ?? 0
      if (el.value && textContainer.value) {
        // Mobile animation TODO: only animate image (smoother)
        mm.add('(max-width: ' + breakpoint + 'px)', () => {
          tl.to(textContainer.value, {
            y: () =>
              (textContainer.value
                ? parseInt(getComputedStyle(textContainer.value).marginTop) * -1
                : 0) / 4,
            scrollTrigger: {
              trigger: el.value,
              start: 'top 80%',
              end: 'bottom 80px',
              scrub: 2,
              invalidateOnRefresh: true,
              markers: false,
            },
          }).to(
            imageContainer.value,
            {
              y: () => -elHeight / 10, // divider = 100 / (image-height[in %]||(image-height[%] + image-top[%]) + - 100%)
              scrollTrigger: {
                trigger: el.value,
                start: 'top 80%',
                end: 'bottom 80px',
                scrub: 1.5,
                invalidateOnRefresh: true,
                markers: false,
              },
            },
            0
          )
        })

        mm.add('(min-width: ' + (breakpoint + 1) + 'px)', () => {
          tl.to(textContainer.value, {
            y: () =>
              (textContainer.value
                ? parseInt(getComputedStyle(textContainer.value).marginTop) * -1
                : 0) / 4,
            scrollTrigger: {
              trigger: el.value,
              start: 'top 90%',
              end: 'bottom 80px',
              scrub: 1.75,
              invalidateOnRefresh: true,
              markers: false,
            },
          }).to(
            imageContainer.value,
            {
              y: () => -elHeight / 5, // divider = 100 / (image-height[in %]||(image-height[%] + image-top[%]) + - 100%)
              scrollTrigger: {
                trigger: el.value,
                start: 'top 90%',
                end: 'bottom 80px',
                scrub: 1.25,
                invalidateOnRefresh: true,
                markers: false,
              },
            },
            0
          )
        })
      }
    },
    (el as any).value
  )
}
</script>
